import {
  type SearchStagingProductBySku as NewProductFoundDto,
  type PresignedProductImage,
  type FullStorefrontProductWithImages as ProductDto,
  type SearchProductBySku as ProductFoundDto,
  type ProductManufacturer as ProductManufacturerDto,
  type FullCatalogManagementProductWithImages as ProductProfileDto,
  type ProductProperty as ProductPropertyDto,
  type ProductSuggestionsResponse as ProductSuggestionsResponseDto,
  type ProductType as ProductTypeDto,
  type ProductUpdateRequest as ProductUpdateDto,
  RelatedProductCategoryNameEnum,
  type AddRelatedProductToCategoryRequest as RelatedProductRequestDto,
  type RemoveRelatedProductFromCategoryRequest as RemoveRelatedProductDto,
  type SetDefaultRelatedProductRequest as SetDefaultRelatedProductDto,
  type ProductApplicableMarginRulesResponse as ProductMarginsDto,
  type CreateProductMarginRuleRequest as CreateProductMarginRuleDto,
  SortOrder,
  ImportProductRequest as ImportProductRequestDto,
} from '@electricjs/core_entity-client';

import type {
  ApiListResponseDto,
  ProductResponseDto,
} from '@externalTypes/general';
import { apiSlice, TagTypes } from '@/redux/slices/apiSlice';
import type { PaginatedResponse } from '@/types/common';
import type { AddImageArgs, ManageImageArgs } from '@/types/images';
import type {
  addNewProductArgs,
  DynamicProductProperty,
  GetProductsArgs,
  GetSuggestionsArgs,
  NewProductFound,
  Product,
  ProductFound,
  ProductLinkArgs,
  ProductManufacturers,
  ProductMarginRule,
  ProductProfile,
  ProductProfileUpdateArgs,
  ProductSearchBySkuArgs,
  ProductsResponse,
  ProductType,
  SearchNewProductsBySkuArgs,
  SetProductSpecificMarginArgs,
  Suggestions,
} from '@/types/products';
import type { QueryParam } from '@/types/queryParamKeys';
import { DynamicFilterKey } from '@/types/storefront';
import { fetchAndDownloadFile } from './helpers/fetchAndDownloadFileHelper';

// Defining the Image DTO while backend is not ready to have it on its model
export type ProductImageDto = PresignedProductImage & {
  image_url: string;
};

// Defining a enum for ProductOriginName (while the backend does not provide it)
export const ProductOriginName = {
  TDSynnex: 'TD Synnex',
  Tecspal: 'Tecspal',
  Zones: 'Zones',
  Electric: 'Electric',
  TDSynnexCanada: 'TD Synnex Canada',
} as const;

export type ProductOriginName =
  (typeof ProductOriginName)[keyof typeof ProductOriginName];

/** Transform dynamic product properties */
const transformDynamicProductProperty = (
  property: ProductPropertyDto
): DynamicProductProperty => ({
  id: property.id,
  slug: property.product_type_property.slug,
  name: property.product_type_property.product_property,
  displayName: property.product_type_property.display_name,
  value: property.value,
  isDisplayedOnStorefront: property.product_type_property.is_displayed,
  isRequired: property.product_type_property.is_required,
  isReadyOnly: property.product_type_property.is_read_only,
});

/** Transform ProductProfile's backend model (ProductProfileDto) to frontend internal model */
const transformProductProfile = (
  productDto: ProductProfileDto
): ProductProfile => ({
  id: productDto.id,
  createdAt: productDto.created_at,
  updatedAt: productDto.updated_at,
  name: productDto.name,
  description: productDto.description,
  productType: productDto.product_type.name,
  countryCode: productDto.country_code,
  manufacturer: {
    id: productDto.product_manufacturer.id,
    name: productDto.product_manufacturer.name,
  },
  productOrigin: {
    id: productDto.product_origin.id,
    name: productDto.product_origin.name,
  },
  manufacturerPartNumber: productDto.manufacturer_part_num,
  vendorURL: productDto.vendor_url,
  vendorSKU: productDto.vendor_sku,
  upc: productDto.upc,
  unspsc: productDto.unspsc,
  price: productDto.price ?? undefined, // converting "null" to "undefined"
  resellerPrice: productDto.reseller_price ?? undefined,
  unitCost: productDto.unit_cost,
  contractPrice: productDto.contract_price,
  map: productDto.min_advertised_price,
  msrp: productDto.msrp,
  status: productDto.status,
  dynamicProperties: productDto.product_properties?.map(property =>
    transformDynamicProductProperty(property)
  ),
  inventory: {
    total: productDto.product_inventory?.total,
    miamiFl: productDto.product_inventory?.inventory_miami_fl,
    tracyCa: productDto.product_inventory?.inventory_tracy_ca,
    romeovilleIl: productDto.product_inventory?.inventory_romeoville_il,
    southavenMs: productDto.product_inventory?.inventory_southaven_ms,
    monroeNj: productDto.product_inventory?.inventory_monroe_nj,
    columbusOh: productDto.product_inventory?.inventory_columbus_oh,
    suwaneeGa: productDto.product_inventory?.inventory_suwanee_ga,
    chinoCa: productDto.product_inventory?.inventory_chino_ca,
    swedesboroNj: productDto.product_inventory?.inventory_swedesboro_nj,
    southBendIn: productDto.product_inventory?.inventory_south_bend_in,
    fortWorthTx: productDto.product_inventory?.inventory_fort_worth_tx,
    fontanaCa: productDto.product_inventory?.inventory_fontana_ca,
    minimumInventory: productDto.product_inventory?.minimum_inventory,
  },
  images: (productDto.product_images as ProductImageDto[])?.map(imageDto => ({
    id: imageDto.id,
    url: imageDto.image_url,
    isPrimary: imageDto.is_primary,
  })),
  relatedProductsByCategory: productDto.related_product_categories
    ?.filter(item => item.name !== RelatedProductCategoryNameEnum.Warranties)
    .map(item => ({
      categoryName: item.name,
      defaultProductId: item.default_product_id,
      products: item.related_products?.map(product =>
        transformProductProfile(product)
      ),
    })),
  availableWarranties: productDto.related_product_categories
    ?.filter(item => item.name === RelatedProductCategoryNameEnum.Warranties)
    .map(item => ({
      defaultWarrantyId: item.default_product_id,
      list: item.related_products?.map(product =>
        transformProductProfile(product)
      ),
    }))
    .pop(),
});

/** Transform Product backend model (ProductDto) to frontend internal model */
const transformProduct = (productDto: ProductDto): Product => ({
  id: productDto.id,
  name: productDto.name,
  description: productDto.description,
  productType: productDto.product_type.name,
  taxCode: productDto.product_type.tax_code ?? '',
  manufacturerName: productDto.product_manufacturer.name,
  manufacturerPartNumber: productDto.manufacturer_part_num,
  vendorSKU: productDto.vendor_sku,
  price: productDto.price ?? undefined,
  currencyCode: productDto.currency_code ?? undefined,
  status: productDto.status,
  dynamicProperties: productDto.product_properties
    // Filtering out empty properties to avoid displaying empty values on Storefront
    ?.filter(property => property.value?.trim() !== '')
    ?.map(property => ({
      slug: property.product_type_property.slug,
      name: property.product_type_property.product_property,
      displayName: property.product_type_property.display_name,
      value: property.value,
      isDisplayedOnStorefront: property.product_type_property.is_displayed,
    })),
  image: (productDto.product_images as ProductImageDto[])?.find(
    image => image.is_primary
  )?.image_url,
  windowsZtpLowestInventory: productDto.ztp_enabled_warehouses
    ? Math.min(
        ...Object.values(productDto.ztp_enabled_warehouses)
          .map(Number)
          .filter(value => value !== 0)
      )
    : undefined,
  relatedProductsByCategory: productDto.related_product_categories
    ?.filter(item => item.name !== RelatedProductCategoryNameEnum.Warranties)
    .map(item => ({
      categoryName: item.name,
      defaultProductId: item.default_product_id,
      products: item.related_products?.map(product =>
        transformProduct(product)
      ),
    })),
  availableWarranties: productDto.related_product_categories
    ?.filter(item => item.name === RelatedProductCategoryNameEnum.Warranties)
    .map(item => ({
      defaultWarrantyId: item.default_product_id,
      list: item.related_products?.map(product => transformProduct(product)),
    }))
    .pop(),
});

export const productApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    //
    // Product profiles (the "full" product model, that contains internal/administrative properties)
    //
    getPaginatedProductProfiles: builder.query<
      PaginatedResponse<ProductProfile>,
      GetProductsArgs
    >({
      query: ({
        offset,
        limit,
        orderBy,
        sortOrder,
        productTypes,
        status,
        dynamicFilters,
        searchTerm,
        countryCode,
        productOriginName,
      }) => {
        //map each product type as a query param
        const productTypesQueryParams: QueryParam[] =
          productTypes?.map(productType => ({
            key: DynamicFilterKey.TYPE,
            value: productType,
          })) ?? [];

        // individualize each dynamic filter as param (repeat the filter type for filters with multiple values)
        const dynamicFiltersQueryParams: QueryParam[] = [];

        dynamicFilters &&
          (Object.keys(dynamicFilters) as DynamicFilterKey[])
            .filter(filterKey => filterKey !== DynamicFilterKey.TYPE) // filtering out the "product_type", as it's already handled separately
            .forEach(filterKey => {
              const filterValues = dynamicFilters[filterKey];

              filterValues?.forEach(filterValue => {
                dynamicFiltersQueryParams.push({
                  key: filterKey,
                  value: filterValue,
                });
              });
            });

        // composing the final query params
        const queryParams: QueryParam[] = [
          { key: 'offset', value: String(offset ?? 0) },
          { key: 'limit', value: String(limit ?? 9) },
          { key: 'order_by', value: orderBy },
          { key: 'sort_order', value: sortOrder },
          { key: 'status', value: status },
          { key: 'search_term', value: searchTerm || undefined },
          { key: 'country_code', value: countryCode || undefined },
          { key: 'product_origin_name', value: productOriginName },
          ...productTypesQueryParams,
          ...dynamicFiltersQueryParams,
        ];

        // converting to query string
        const queryString = queryParams
          .filter(param => param.value !== undefined)
          .map(param => `${param.key}=${encodeURIComponent(param.value ?? '')}`)
          .join('&');

        return {
          // manually passing query string in order to handle repetitive params (backend doesn't support array of values)
          url: `v3/products/catalog?${queryString}`,
        };
      },
      transformResponse: (
        dtoResponse: ApiListResponseDto<ProductProfileDto>
      ) => {
        return {
          results: dtoResponse.results.map(productDto =>
            transformProductProfile(productDto)
          ),
          total: dtoResponse.total ?? 0,
        };
      },
      providesTags: [TagTypes.ProductProfile],
    }),
    downloadCatalogProducts: builder.query<
      void,
      Omit<GetProductsArgs, 'offset' | 'limit'>
    >({
      queryFn: async (
        {
          orderBy,
          sortOrder,
          productTypes,
          status,
          searchTerm,
          countryCode,
          productOriginName,
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        //map each product type as a query param
        const productTypesQueryParams: QueryParam[] =
          productTypes?.map(productType => ({
            key: DynamicFilterKey.TYPE,
            value: productType,
          })) ?? [];

        // composing the final query params
        const queryParams: QueryParam[] = [
          { key: 'order_by', value: orderBy },
          { key: 'sort_order', value: sortOrder },
          { key: 'status', value: status },
          { key: 'search_term', value: searchTerm || undefined },
          { key: 'country_code', value: countryCode || undefined },
          { key: 'product_origin_name', value: productOriginName },
          ...productTypesQueryParams,
        ];

        // converting to query string
        const queryString = queryParams
          .filter(param => param.value !== undefined)
          .map(param => `${param.key}=${encodeURIComponent(param.value ?? '')}`)
          .join('&');

        return await fetchAndDownloadFile(
          baseQuery as (arg: unknown) => unknown,
          // manually building the query string in order to handle repetitive params (backend doesn't support array of values)
          `v3/products/catalog/download?${queryString}`,
          'product-catalog.csv'
        );
      },
    }),
    getProductProfile: builder.query<ProductProfile, string>({
      query: productId => {
        return {
          url: `v3/products/catalog/${productId}`,
        };
      },
      transformResponse: (responseDto: ProductProfileDto) => {
        return transformProductProfile(responseDto);
      },
      providesTags: [TagTypes.ProductProfile],
    }),
    updateProductProfile: builder.mutation<void, ProductProfileUpdateArgs>({
      query: product => {
        const body: ProductUpdateDto = {
          name: product.name,
          description: product.description,
          product_manufacturer_id: product.manufacturerId,
          vendor_url: product.vendorURL,
          status: product.status,
          price: product.price,
          product_properties: product.dynamicProperties?.map(property => ({
            id: property.id,
            value: property.value,
          })),
          minimum_inventory: product.minimumInventory,
        };

        return {
          url: `v3/products/catalog/${product.id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TagTypes.ProductProfile, TagTypes.Product],
    }),
    getAIDescription: builder.query<string, string>({
      query: productId => {
        return {
          url: `v3/products/catalog/${productId}/ai_description`,
        };
      },
      transformResponse: (responseDto: { description: string }) => {
        return responseDto.description;
      },
    }),
    addProductImage: builder.mutation<void, AddImageArgs>({
      query: ({ baseResourceId, file }) => {
        // The file to be uploaded needs to be appended in this formData with the key "files"
        const formData = new FormData();
        formData.append('files', file);

        return {
          url: `v3/products/catalog/${baseResourceId}/images`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [TagTypes.ProductProfile, TagTypes.Product],
    }),
    defineImageAsPrimary: builder.mutation<void, ManageImageArgs>({
      query: ({ baseResourceId, imageId }) => {
        return {
          url: `v3/products/catalog/${baseResourceId}/images/${imageId}/set_primary`,
          method: 'PATCH',
        };
      },
      invalidatesTags: [TagTypes.ProductProfile, TagTypes.Product],
    }),
    deleteProductImage: builder.mutation<void, ManageImageArgs>({
      query: ({ baseResourceId, imageId }) => {
        return {
          url: `v3/products/catalog/${baseResourceId}/images/${imageId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [TagTypes.ProductProfile, TagTypes.Product],
    }),
    searchNewProductsBySku: builder.query<
      NewProductFound[],
      SearchNewProductsBySkuArgs
    >({
      query: ({ vendorSKU, countryCode }) => {
        return {
          url: `v3/products/catalog/staging/search_by_sku/${vendorSKU}`,
          params: {
            country_code: countryCode,
          },
        };
      },
      transformResponse: (response: NewProductFoundDto[]) => {
        return response.map(productFoundDto => {
          return {
            vendorSKU: productFoundDto.vendor_sku,
            name: productFoundDto.name,
            inferredProductType: productFoundDto.product_type_name,
            manufacturerName: productFoundDto.manufacturer_name,
            manufacturerPartNumber: productFoundDto.manufacturer_part_number,
            msrp: productFoundDto.msrp,
            model: productFoundDto.model,
            alreadyAdded: productFoundDto.already_exists,
          };
        });
      },
    }),
    addNewProductBySku: builder.mutation<string, addNewProductArgs>({
      query: ({ vendorSKU, productType, countryCode }) => {
        const body: ImportProductRequestDto = {
          vendor_sku: vendorSKU,
          product_type_name: productType,
          country_code: countryCode,
        };

        return {
          url: `v3/products/catalog`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (response: { id: string }) => {
        return response.id; // returns the product's ID of the new product
      },
      invalidatesTags: [TagTypes.ProductProfile],
    }),
    searchProductsBySku: builder.query<ProductFound[], ProductSearchBySkuArgs>({
      query: ({
        vendorSKU,
        productType,
        manufacturerId,
        countryCode,
        productOriginName,
      }) => {
        return {
          url: `v3/products/catalog/search_by_sku/${vendorSKU}`,
          params: {
            product_type_name: productType,
            manufacturer_id: manufacturerId,
            country_code: countryCode,
            product_origin_name: productOriginName,
          },
        };
      },
      transformResponse: (response: ProductFoundDto[]) => {
        return response.map(productFoundDto => {
          return {
            id: productFoundDto.product_id,
            vendorSKU: productFoundDto.vendor_sku ?? '',
            name: productFoundDto.name,
            productType: productFoundDto.product_type_name,
            manufacturerName: productFoundDto.manufacturer_name,
            manufacturerPartNumber: productFoundDto.manufacturer_part_num,
            price: productFoundDto.price ?? undefined,
          };
        });
      },
    }),
    linkRelatedProduct: builder.mutation<void, ProductLinkArgs>({
      query: ({
        baseProductId,
        linkedProductId,
        category,
        isDefaultProduct,
      }) => {
        const body: RelatedProductRequestDto = {
          product_id: linkedProductId,
          category,
          is_default: isDefaultProduct ?? false,
        };

        return {
          url: `v3/products/catalog/${baseProductId}/related_products`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TagTypes.ProductProfile, TagTypes.Product],
    }),
    unlinkRelatedProduct: builder.mutation<void, ProductLinkArgs>({
      query: ({ baseProductId, linkedProductId, category }) => {
        const body: RemoveRelatedProductDto = {
          related_product_id: linkedProductId,
          category,
        };

        return {
          url: `v3/products/catalog/${baseProductId}/related_products/unlink`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TagTypes.ProductProfile, TagTypes.Product],
    }),
    setDefaultRelatedProduct: builder.mutation<void, ProductLinkArgs>({
      query: ({ baseProductId, linkedProductId, category }) => {
        const body: SetDefaultRelatedProductDto = {
          related_product_id: linkedProductId,
          category,
        };

        return {
          url: `v3/products/catalog/${baseProductId}/related_products/default`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TagTypes.ProductProfile, TagTypes.Product],
    }),

    //
    // Product (the simple model, used by Storefront and Gigawatt)
    //
    getPaginatedProducts: builder.query<ProductsResponse, GetProductsArgs>({
      query: ({
        offset,
        limit,
        orderBy,
        sortOrder,
        productTypes,
        dynamicFilters,
        windowsZtpAvailable,
        countryCode,
        productOriginName,
      }) => {
        //map each product type as a query param
        const productTypesQueryParams: QueryParam[] =
          productTypes?.map(productType => ({
            key: DynamicFilterKey.TYPE,
            value: productType,
          })) ?? [];

        // individualize each dynamic filter as param (repeat the filter type for filters with multiple values)
        const dynamicFiltersQueryParams: QueryParam[] = [];

        dynamicFilters &&
          (Object.keys(dynamicFilters) as DynamicFilterKey[])
            .filter(filterKey => filterKey !== DynamicFilterKey.TYPE) // filtering out the "product_type", as it's already handled separately
            .forEach(filterKey => {
              const filterValues = dynamicFilters[filterKey];

              filterValues?.forEach(filterValue => {
                dynamicFiltersQueryParams.push({
                  key: filterKey,
                  value: filterValue,
                });
              });
            });

        // composing the final query params
        const queryParams: QueryParam[] = [
          { key: 'offset', value: String(offset ?? 0) },
          { key: 'limit', value: String(limit ?? 9) },
          { key: 'order_by', value: orderBy },
          { key: 'sort_order', value: sortOrder },
          ...productTypesQueryParams,
          ...dynamicFiltersQueryParams,
          {
            key: 'windows_ztp_available',
            value: windowsZtpAvailable ? 'true' : undefined,
          },
          {
            key: 'country_code',
            value: countryCode,
          },
          {
            key: 'product_origin_name',
            value: productOriginName,
          },
        ];

        // converting to query string
        const queryString = queryParams
          .filter(param => param.value !== undefined)
          .map(param => `${param.key}=${encodeURIComponent(param.value ?? '')}`)
          .join('&');

        return {
          // manually passing query string in order to handle repetitive params (backend doesn't support array of values)
          url: `v3/products?${queryString}`,
        };
      },
      transformResponse: (dtoResponse: ProductResponseDto) => {
        return {
          paginatedResponse: {
            results: dtoResponse.paginated_response.results.map(productDto =>
              transformProduct(productDto)
            ),
            total: dtoResponse.paginated_response.total ?? 0,
          },
          filters: dtoResponse.filters,
        };
      },
      providesTags: [TagTypes.Product],
    }),
    getProduct: builder.query<Product, string>({
      query: productId => {
        return {
          url: `v3/products/${productId}`,
        };
      },
      transformResponse: (responseDto: ProductDto) => {
        return transformProduct(responseDto);
      },
      providesTags: [TagTypes.Product],
    }),

    //
    // Product suggestions
    //
    getSuggestions: builder.query<Suggestions, GetSuggestionsArgs>({
      query: ({
        organizationId,
        employeeId,
        productTypes,
        windowsZtpAvailable,
        countryCode,
        productOriginName,
      }) => {
        //map each product type as a query param
        const productTypesQueryParams: QueryParam[] =
          productTypes?.map(productType => ({
            key: DynamicFilterKey.TYPE,
            value: productType,
          })) ?? [];

        // composing the final query params
        const queryParams: QueryParam[] = [
          { key: 'limit', value: '15' }, // using the same backend's defaults for now
          { key: 'sort_order', value: SortOrder.Desc },
          ...productTypesQueryParams,
          {
            key: 'windows_ztp_available',
            value: windowsZtpAvailable ? 'true' : undefined,
          },
          {
            key: 'country_code',
            value: countryCode,
          },
          {
            key: 'product_origin_name',
            value: productOriginName,
          },
        ];

        // converting to query string
        const queryString = queryParams
          .filter(param => param.value !== undefined)
          .map(param => `${param.key}=${encodeURIComponent(param.value ?? '')}`)
          .join('&');

        return {
          // manually passing query string in order to handle repetitive params (backend doesn't support array of values)
          url: `v3/organizations/${organizationId}/employee/${employeeId}/products/suggestions?${queryString}`,
        };
      },

      transformResponse: (responseDto: ProductSuggestionsResponseDto) => {
        return {
          companyProducts: responseDto.company_products.map(product =>
            transformProduct(product)
          ),
          allEmployeeGroupsProducts:
            responseDto.all_employee_group_products.map(product =>
              transformProduct(product)
            ),
          employeeGroups: responseDto.employee_groups.map(group => ({
            id: group.id,
            name: group.name,
            products: group.products.map(product => transformProduct(product)),
          })),
        };
      },
      providesTags: [TagTypes.Product, TagTypes.ProductSuggestions],
    }),

    //
    // Product margin rules
    //
    getProductMarginRules: builder.query<ProductMarginRule[], string>({
      query: productId => {
        return {
          url: `/v3/internal/products/${productId}/margin_rules`,
        };
      },
      transformResponse: (response: ProductMarginsDto) => {
        return response?.applicable_rules?.map(rule => {
          return {
            id: rule.rule_id,
            type: rule.type,
            value: rule.margin_percent,
            priority: rule.priority,
          };
        });
      },
      providesTags: [TagTypes.ProductMarginRule],
    }),
    setProductSpecificMargin: builder.mutation<
      void,
      SetProductSpecificMarginArgs
    >({
      query: ({ marginValue, productId }) => {
        // this endpoint can edit other types of margins, but we only let the user edit the "product specific" one through the UI
        const body: CreateProductMarginRuleDto = {
          margin_percent: marginValue,
          product_id: productId, // editing only the "product specific" margin
        };

        return {
          url: `v3/internal/products/margin_rules`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: result =>
        result !== undefined // invalidate only if the mutation was successful
          ? [
              TagTypes.ProductMarginRule,
              TagTypes.ProductProfile, // invalidating other tags because product's price may change
              TagTypes.Product,
            ]
          : [],
    }),
    deleteProductSpecificMargin: builder.mutation<void, string>({
      query: ruleId => {
        // this endpoint can delete other types of margins, but we only let the user delete the "product specific" one through the UI

        return {
          url: `/v3/internal/products/margin_rules/${ruleId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: result =>
        result !== undefined // invalidate only if the mutation was successful
          ? [
              TagTypes.ProductMarginRule,
              TagTypes.ProductProfile, // invalidating other tags because product's price may change
              TagTypes.Product,
            ]
          : [],
    }),

    //
    // Product types, manufacturers, etc
    //
    getOrganizationProductTypes: builder.query<ProductType[], string>({
      query: organizationId => {
        return {
          url: `/v3/organizations/${organizationId}/products/types`,
        };
      },
      transformResponse: (response: ProductTypeDto[]) => {
        return response.map(productTypeDto => {
          return {
            id: productTypeDto.id,
            name: productTypeDto.name,
            taxCode: productTypeDto.tax_code,
            organizationId: productTypeDto.organization_id,
            allowsWarehousing: productTypeDto.allows_warehousing,
            isVirtual: productTypeDto.is_virtual,
            taaApplicable: productTypeDto.taa_applicable,
          };
        });
      },
    }),
    getGlobalProductTypes: builder.query<ProductType[], void>({
      query: () => {
        return {
          url: '/v3/products/types',
        };
      },
      transformResponse: (response: ProductTypeDto[]) => {
        return response.map(productTypeDto => {
          return {
            id: productTypeDto.id,
            name: productTypeDto.name,
            taxCode: productTypeDto.tax_code,
            allowsWarehousing: productTypeDto.allows_warehousing,
            isVirtual: productTypeDto.is_virtual,
            taaApplicable: productTypeDto.taa_applicable,
          };
        });
      },
    }),
    getProductManufacturers: builder.query<ProductManufacturers[], void>({
      query: () => {
        return {
          url: '/v3/products/manufacturers',
        };
      },
      transformResponse: (response: ProductManufacturerDto[]) => {
        return response.map(productManufacturerDto => {
          return {
            id: productManufacturerDto.id,
            name: productManufacturerDto.name,
          };
        });
      },
    }),
  }),
});

// export auto-generated hooks
export const {
  useGetPaginatedProductProfilesQuery,
  useLazyDownloadCatalogProductsQuery,
  useGetProductProfileQuery,
  useLazyGetAIDescriptionQuery,
  useUpdateProductProfileMutation,
  useAddProductImageMutation,
  useDefineImageAsPrimaryMutation,
  useDeleteProductImageMutation,
  useLazySearchNewProductsBySkuQuery,
  useAddNewProductBySkuMutation,
  useLazySearchProductsBySkuQuery,
  useLinkRelatedProductMutation,
  useUnlinkRelatedProductMutation,
  useSetDefaultRelatedProductMutation,
  useGetPaginatedProductsQuery,
  useLazyGetPaginatedProductsQuery,
  useGetProductQuery,
  useLazyGetProductQuery,
  useGetOrganizationProductTypesQuery,
  useGetGlobalProductTypesQuery,
  useGetProductManufacturersQuery,
  useGetSuggestionsQuery,
  useGetProductMarginRulesQuery,
  useSetProductSpecificMarginMutation,
  useDeleteProductSpecificMarginMutation,
} = productApiSlice;
